import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';

export function useSendPrompt(serverId: string, sessionId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (message: string) => api.sessions.prompt(serverId, sessionId, message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['session', serverId, sessionId] });
      queryClient.invalidateQueries({ queryKey: ['session-history', serverId, sessionId] });
    },
  });
}

export function useRespondToQuestion(serverId: string, sessionId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ promptId, response }: { promptId: string; response: string }) =>
      api.sessions.respond(serverId, sessionId, promptId, response),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['session', serverId, sessionId] });
      queryClient.invalidateQueries({ queryKey: ['session-history', serverId, sessionId] });
    },
  });
}

export function useRestoreSession(serverId: string, sessionId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.sessions.restore(serverId, sessionId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['session', serverId, sessionId] });
      queryClient.invalidateQueries({ queryKey: ['sessions', serverId] });
      queryClient.invalidateQueries({ queryKey: ['session-history', serverId, sessionId] });
    },
  });
}

export function useInterruptSession(serverId: string, sessionId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.sessions.interrupt(serverId, sessionId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['session', serverId, sessionId] });
    },
  });
}

export function useSwitchModel(serverId: string, sessionId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (model: string) => api.sessions.switchModel(serverId, sessionId, model),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['session', serverId, sessionId] });
      queryClient.invalidateQueries({ queryKey: ['session-history', serverId, sessionId] });
    },
  });
}

/** What `/` completes: the commands and skills of the server's sessions, read once in a while */
export function useCommands(serverId: string, enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['commands', serverId],
    queryFn: async () => (await api.sessions.commands(serverId)).commands,
    enabled,
    // An empty list (no session started since the agent did) is asked again next time
    staleTime: (q) => ((q.state.data as string[] | undefined)?.length ? 5 * 60_000 : 0),
  });
}

/** One folder of the workspace, for `@` completion */
export function useWorkspaceFolder(serverId: string, path: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['workspace-folder', serverId, path],
    queryFn: async () => (await api.workspace.tree(serverId, path || undefined, 1)).entries,
    enabled,
    staleTime: 60_000,
  });
}

/** Gives the session a secret (ADR-043 section 2); its value lives in the form until sent, never in a cache */
export function useGiveSecret(serverId: string, sessionId: string) {
  const api = useApiClient();
  return useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) => api.sessions.giveSecret(serverId, sessionId, name, value),
    gcTime: 0,
  });
}

/** The facets the index already holds for the session; rarely changing: read every few minutes */
export function useSessionFacets(serverId: string, sessionId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['session-facets', serverId, sessionId], queryFn: () => api.sessions.facets(serverId, sessionId), staleTime: 300_000, refetchInterval: 600_000 });
}

/** The sessions of a facet (`dimension:value`), for the server's list; nothing asked without one */
export function useFacetSessions(serverId: string, facet: string) {
  const api = useApiClient();
  const at = facet.indexOf(':');
  return useQuery({
    queryKey: ['facet-sessions', serverId, facet], enabled: at > 0, staleTime: 120_000,
    queryFn: () => api.sessions.byFacet(serverId, facet.slice(0, at), facet.slice(at + 1)),
  });
}
